import { createPortal } from "react-dom";
import { useEffect, useRef, useState, Fragment } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, Undo2, Redo2, ChevronDown, ArrowLeftRight, Maximize2 } from "lucide-react";
import { DragDropContext } from "@hello-pangea/dnd";
import { Switch } from "@/components/ui/switch";
import { SECTION_GROUPS, SUBTYPE_META, defaultSectionForSubtype } from "@/components/sectionTypes";
import { osBinding, matchesShortcut } from "@/lib/shortcuts";
import { useUnsavedGuard } from "@/lib/useUnsavedGuard";
import BlogEditorMetadata from "@/components/editor/BlogEditorMetadata";
import BlogEditorTOC from "@/components/editor/BlogEditorTOC";
import BlogEditorPreview from "@/components/editor/BlogEditorPreview";
import BlogEditorInlineSection from "@/components/editor/BlogEditorInlineSection";

export default function BlogEditor({ blogId, open, onOpenChange, onSaved }) {
  const [blog, setBlog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reuploading, setReuploading] = useState(false);
  const [authors, setAuthors] = useState([]);
  const [veSettings, setVeSettings] = useState(null);
  const [editorUi, setEditorUi] = useState({ flip_panes: false, editor_full_pane: false, preview_ratio: 50 });
  const [dirty, setDirty] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  // Guard against leaving the page while there are unsaved edits — intercepts
  // React Router navigation AND browser tab close / reload.
  useUnsavedGuard(open && dirty);
  // The section currently open in the bottom-left inline editor. `null` when
  // the left pane is showing the overview (metadata + TOC) only.
  const [editingSectionId, setEditingSectionId] = useState(null);
  const previewRefs = useRef({});
  const previewPaneRef = useRef(null);
  const sectionEditorRef = useRef(null);
  // Undo/redo stacks for structural edits (add/delete/reorder/level/docx replace).
  // Text edits inside inputs and the rich text editor rely on their own native undo.
  const historyRef = useRef({ past: [], future: [] });
  const [historyTick, setHistoryTick] = useState(0);
  // Stacked-toast tracking for section deletes — see removeSectionWithToast.
  const pendingDeleteRef = useRef({ toastId: null, count: 0 });
  // Global confirm-dialog state for every destructive action inside the editor.
  const [confirmState, setConfirmState] = useState({ open: false, title: "", body: "", onConfirm: null });
  const confirmDelete = (title, body, onConfirm) => setConfirmState({ open: true, title, body, onConfirm });
  const snap = (b) => JSON.parse(JSON.stringify(b));
  const pushHistory = (prev) => {
    if (!prev) return;
    historyRef.current.past.push(snap(prev));
    if (historyRef.current.past.length > 50) historyRef.current.past.shift();
    historyRef.current.future = [];
    setHistoryTick(t => t + 1);
  };

  const scrollPreviewTo = (id) => {
    const target = previewRefs.current[id];
    const container = previewPaneRef.current;
    if (target && container) {
      const top = target.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - 12;
      container.scrollTo({ top, behavior: "smooth" });
    }
  };

  const openSection = (id) => {
    setEditingSectionId(id);
    // Give the section editor a beat to mount before scrolling to it.
    setTimeout(() => sectionEditorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 40);
  };

  useEffect(() => { if (open) api.get("/authors").then(r => setAuthors(r.data)); }, [open]);
  useEffect(() => { if (open) api.get("/site-settings/visual-editor").then(r => setVeSettings(r.data)).catch(() => setVeSettings({})); }, [open]);
  useEffect(() => { if (open) api.get("/site-settings/blog-editor-ui").then(r => setEditorUi(r.data)).catch(() => {}); }, [open]);
  // Debounced save so slider drags don't spam the backend.
  const uiSaveTimerRef = useRef(null);
  const setEditorUiField = (patch) => {
    setEditorUi(prev => {
      const next = { ...prev, ...patch };
      if (uiSaveTimerRef.current) clearTimeout(uiSaveTimerRef.current);
      uiSaveTimerRef.current = setTimeout(() => {
        api.put("/site-settings/blog-editor-ui", next).catch(() => {});
      }, 300);
      return next;
    });
  };

  useEffect(() => {
    if (!open || !blogId) return;
    api.get("/blogs").then(async r => {
      const meta = r.data.find(b => b.blog_id === blogId);
      if (!meta) return;
      const full = await api.get(`/blogs/${meta.slug}`);
      setBlog(full.data);
      setEditingSectionId(null);
      historyRef.current = { past: [], future: [] };
      setHistoryTick(t => t + 1);
    });
  }, [open, blogId]);

  // Structural undo/redo — snapshots pushed by add/delete/reorder/level/docx.
  // Text-typing inside inputs and the RichTextEditor is skipped (handler bails
  // when target is INPUT/TEXTAREA/contenteditable) so their native undo works.
  const undo = () => {
    if (!blog) return;
    const { past, future } = historyRef.current;
    if (past.length === 0) return;
    const prev = past.pop();
    future.push(snap(blog));
    if (future.length > 50) future.shift();
    if (editingSectionId && !prev.sections.some(s => s.id === editingSectionId)) {
      setEditingSectionId(null);
    }
    setBlog(prev);
    setDirty(true);
    setHistoryTick(t => t + 1);
    toast("Undid last change");
  };
  const redo = () => {
    if (!blog) return;
    const { past, future } = historyRef.current;
    if (future.length === 0) return;
    const next = future.pop();
    past.push(snap(blog));
    if (past.length > 50) past.shift();
    if (editingSectionId && !next.sections.some(s => s.id === editingSectionId)) {
      setEditingSectionId(null);
    }
    setBlog(next);
    setDirty(true);
    setHistoryTick(t => t + 1);
    toast("Redid change");
  };

  useEffect(() => {
    if (!open) return;
    // Read shortcut settings once per open and keep them in memory.
    let cancelled = false;
    let cfg = null;
    api.get("/site-settings/keyboard-shortcuts").then(r => {
      if (!cancelled) cfg = r.data.shortcuts || {};
    }).catch(() => {});

    const onKey = (e) => {
      const t = e.target;
      const tag = t?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || t?.isContentEditable) return;
      if (!cfg) return;
      const undoCfg = cfg["editor-undo"];
      const redoCfg = cfg["editor-redo"];
      if (undoCfg?.enabled && matchesShortcut(e, osBinding(undoCfg))) { e.preventDefault(); undo(); return; }
      if (redoCfg?.enabled && matchesShortcut(e, osBinding(redoCfg))) { e.preventDefault(); redo(); return; }
    };
    window.addEventListener("keydown", onKey);
    return () => { cancelled = true; window.removeEventListener("keydown", onKey); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, blog, editingSectionId]);

  useEffect(() => {
    if (!confirmState.open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setConfirmState({ open: false, title: "", body: "", onConfirm: null });
      }
    };
    // Capture so we run before Radix's Escape handler (which we suppress
    // via onEscapeKeyDown preventDefault while a confirm is open).
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [confirmState.open]);

  useEffect(() => {
    if (!confirmClose) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setConfirmClose(false);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [confirmClose]);

  if (!blog) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl"><div className="p-6 text-slate-500">Loading…</div></DialogContent>
      </Dialog>
    );
  }

  const updateField = (k, v) => { setBlog({ ...blog, [k]: v }); setDirty(true); };
  const updateSection = (idx, k, v) => {
    // Level changes are structural — snapshot before applying. Heading/html
    // rely on the input/RTE's own undo so we don't spam history on every keystroke.
    if (k === "level") pushHistory(blog);
    const arr = [...blog.sections];
    arr[idx] = { ...arr[idx], [k]: v };
    setBlog({ ...blog, sections: arr });
    setDirty(true);
  };
  const addSection = (subtype = "prose") => {
    pushHistory(blog);
    const id = `sec-${Date.now()}`;
    const meta = SUBTYPE_META[subtype] || SUBTYPE_META.prose;
    const def = defaultSectionForSubtype(subtype, veSettings);
    setBlog({
      ...blog,
      sections: [...blog.sections, {
        id, level: 2, heading: def.heading || `New ${meta.label.toLowerCase()}`,
        type: meta.type, subtype, html: def.html, data: def.data,
      }],
    });
    setDirty(true);
    openSection(id);
  };
  const removeSection = (id) => {
    pushHistory(blog);
    const arr = blog.sections.filter(s => s.id !== id);
    setBlog({ ...blog, sections: arr });
    if (editingSectionId === id) setEditingSectionId(null);
    setDirty(true);
  };
  // Gmail-style "Section deleted · Undo" toast that STACKS when multiple
  // deletes happen back-to-back. While the previous toast is still visible,
  // deleting again reuses the same toast id (so Sonner updates it in place)
  // and increments a counter. The Undo action pops that many entries off the
  // history stack in one motion.
  const removeSectionWithToast = (id, heading) => {
    removeSection(id);
    const s = pendingDeleteRef.current;
    s.count += 1;
    const isBatch = s.count > 1;
    const returned = toast(
      isBatch ? `${s.count} sections deleted` : "Section deleted",
      {
        id: s.toastId ?? undefined,
        description: isBatch
          ? `Including "${heading || "Untitled section"}"`
          : (heading || "Untitled section"),
        duration: 6000,
        action: {
          label: isBatch ? "Undo all" : "Undo",
          onClick: () => {
            // Pop the last N states off the history stack and restore the
            // FIRST (oldest) popped state in a single setBlog — cleanly
            // rewinds all N deletes without React batching + closure oddities
            // that would occur if we simply called undo() N times.
            const n = pendingDeleteRef.current.count;
            const { past, future } = historyRef.current;
            const takeCount = Math.min(n, past.length);
            if (takeCount === 0) return;
            const popped = past.splice(past.length - takeCount, takeCount);
            const target = popped[0]; // oldest popped = pre-first-delete
            // Push the intermediate states to future (in reverse) so redo
            // could step forward again, matching the semantics of N undos.
            for (let i = popped.length - 1; i >= 1; i--) {
              future.push(snap(popped[i]));
              if (future.length > 50) future.shift();
            }
            setBlog(target);
            setDirty(true);
            setHistoryTick((t) => t + 1);
            pendingDeleteRef.current = { toastId: null, count: 0 };
            toast(takeCount === 1 ? "Restored" : `Restored ${takeCount} sections`);
          },
        },
        onAutoClose: () => { pendingDeleteRef.current = { toastId: null, count: 0 }; },
        onDismiss: () => { pendingDeleteRef.current = { toastId: null, count: 0 }; },
      }
    );
    s.toastId = returned;
  };
  // Insert a new section at a specific position (used by the hover "+" dividers
  // between TOC rows and preview sections). index 0 = before the first,
  // blog.sections.length = after the last (same as addSection).
  const insertSectionAt = (index, subtype = "prose") => {
    pushHistory(blog);
    const id = `sec-${Date.now()}`;
    const meta = SUBTYPE_META[subtype] || SUBTYPE_META.prose;
    const def = defaultSectionForSubtype(subtype, veSettings);
    const arr = [...blog.sections];
    arr.splice(index, 0, {
      id, level: 2, heading: def.heading || `New ${meta.label.toLowerCase()}`,
      type: meta.type, subtype, html: def.html, data: def.data,
    });
    setBlog({ ...blog, sections: arr });
    setDirty(true);
    openSection(id);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    // Only allow reorder within the same droppable — cross-pane drags between
    // the TOC and preview would use raw indices from different lists.
    if (result.source.droppableId !== result.destination.droppableId) return;
    if (result.destination.index === result.source.index) return;
    pushHistory(blog);
    const arr = [...blog.sections];
    const [moved] = arr.splice(result.source.index, 1);
    arr.splice(result.destination.index, 0, moved);
    setBlog({ ...blog, sections: arr });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/blogs/${blog.blog_id}`, {
        title: blog.title,
        slug: blog.slug || undefined,
        subtitle: blog.subtitle || null,
        author_id: blog.author_id || null,
        author_name: blog.author_name || null,
        excerpt: blog.excerpt || "",
        release_date: blog.release_date || null,
        first_published: blog.first_published || null,
        last_updated: blog.last_updated || null,
        seo_description: blog.seo_description || null,
        primary_module: blog.primary_module || null,
        secondary_modules: blog.secondary_modules || [],
        header_image_url: blog.header_image_url || null,
        header_image_alt: blog.header_image_alt || null,
        header_image_filename: blog.header_image_filename || null,
        summary_321: blog.summary_321 || null,
        bibliography: blog.bibliography || [],
        sections: blog.sections,
      });
      toast.success("Blog saved");
      setDirty(false);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const replaceDocx = async (file) => {
    if (!file) return;
    setReuploading(true);
    const prev = blog;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const resp = await fetch(`${backend}/api/blogs/${blog.blog_id}/reupload`, {
        method: "POST", body: fd, credentials: "include",
      });
      if (!resp.ok) {
        let detail = "";
        try { detail = (await resp.json()).detail || ""; } catch { try { detail = (await resp.text()).slice(0,200); } catch { /* ignore */ } }
        throw new Error(detail || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      pushHistory(prev);
      setBlog(data);
      setEditingSectionId(null);
      toast.success(".docx re-parsed — review and save");
    } catch (e) {
      toast.error(e.message || "Reupload failed");
    } finally { setReuploading(false); }
  };

  const editingSection = blog?.sections?.find(s => s.id === editingSectionId);
  const editingIndex = blog?.sections?.findIndex(s => s.id === editingSectionId);

  const canUndo = historyRef.current.past.length > 0;
  const canRedo = historyRef.current.future.length > 0;
  // Read historyTick so the toolbar buttons re-render on history mutations.
  void historyTick;

  // Shared dropdown content for both "Add section" and the hover "+ Insert"
  // dividers. Groups: Editorial · Media · Data — each showing its subtypes
  // with an icon and short hint.
  const SubtypeMenu = ({ onPick }) => (
    <DropdownMenuContent align="start" className="w-64">
      {SECTION_GROUPS.map((g, gi) => (
        <Fragment key={g.type}>
          {gi > 0 && <DropdownMenuSeparator/>}
          <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-500">{g.label}</DropdownMenuLabel>
          {g.items.map(it => {
            const Icon = it.Icon;
            return (
              <DropdownMenuItem
                key={it.subtype}
                data-testid={`subtype-menu-${it.subtype}`}
                onSelect={() => onPick(it.subtype)}
                className="gap-3 py-2">
                <Icon className="w-4 h-4 text-sky-700 shrink-0"/>
                <div className="min-w-0">
                  <div className="text-sm font-medium">{it.label}</div>
                  <div className="text-[11px] text-slate-500 truncate">{it.hint}</div>
                </div>
              </DropdownMenuItem>
            );
          })}
        </Fragment>
      ))}
    </DropdownMenuContent>
  );

  // Hover-to-appear "+ Insert" divider between sections. The outer wrapper has
  // ZERO layout height so it doesn't add space between sections (the preview
  // matches the final rendered blog spacing). A small absolute-positioned hit
  // area straddles the section boundary and reveals the pill + line on hover.
  const InsertDivider = ({ index, variant }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div
          data-testid={`insert-section-${variant}-${index}`}
          title="Insert section here"
          className="relative h-0 z-10"
        >
          <div className={`absolute left-0 right-0 group/insert cursor-pointer flex items-center justify-center ${
            variant === "preview" ? "-top-2 h-4 mx-6" : "-top-1 h-2"
          }`}>
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-sky-400 opacity-0 group-hover/insert:opacity-100 transition-opacity pointer-events-none"/>
            <span className="relative z-10 opacity-0 group-hover/insert:opacity-100 transition-opacity inline-flex items-center gap-1 bg-sky-600 hover:bg-sky-500 text-white text-[10px] uppercase tracking-widest font-semibold rounded-full px-2 py-0.5 shadow">
              <Plus className="w-3 h-3"/>Insert
            </span>
          </div>
        </div>
      </DropdownMenuTrigger>
      <SubtypeMenu onPick={(sub) => insertSectionAt(index, sub)}/>
    </DropdownMenu>
  );

  return (<>
    <Dialog open={open} onOpenChange={(v) => {
      if (!v && dirty) { setConfirmClose(true); return; }
      onOpenChange(v);
    }}>
      <DialogContent
        className="max-w-[92vw] w-[92vw] h-[92vh] max-h-[92vh] p-0 flex flex-col overflow-hidden"
        onInteractOutside={(e) => {
          // Ignore clicks landing on Sonner toasts — otherwise clicking the
          // "Undo" action on our delete toast would register as "outside the
          // dialog", close the Blog Editor, and swallow the undo.
          const t = e.target;
          if (t && (t.closest?.("[data-sonner-toaster]") || t.closest?.("[data-sonner-toast]"))) {
            e.preventDefault();
            return;
          }
          // While our custom confirm-delete modal (or the unsaved-changes
          // modal) is open, Radix would otherwise treat clicks inside them
          // as "outside content" and close the editor. Block that.
          if (confirmState.open || confirmClose) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (confirmState.open || confirmClose) e.preventDefault();
        }}
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-200 shrink-0">
          <DialogTitle className="font-heading flex items-center gap-2">
            Edit blog
            {dirty && <span className="text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 rounded px-1.5 py-0.5">Unsaved</span>}
            <div className="ml-auto mr-8 flex items-center gap-1">
              <button
                data-testid="editor-undo"
                onClick={undo}
                disabled={!canUndo}
                title="Undo (Ctrl+Z)"
                aria-label="Undo"
                className="w-8 h-8 grid place-items-center rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-30 disabled:pointer-events-none transition-colors">
                <Undo2 className="w-4 h-4"/>
              </button>
              <button
                data-testid="editor-redo"
                onClick={redo}
                disabled={!canRedo}
                title="Redo (Ctrl+Shift+Z)"
                aria-label="Redo"
                className="w-8 h-8 grid place-items-center rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-30 disabled:pointer-events-none transition-colors">
                <Redo2 className="w-4 h-4"/>
              </button>
            </div>
          </DialogTitle>
        </DialogHeader>

        <DragDropContext onDragEnd={onDragEnd}>
          <div
            className="flex-1 min-h-0 grid grid-cols-1 lg:grid-flow-col divide-x divide-slate-200"
            style={{
              gridTemplateColumns: editorUi.flip_panes
                ? `minmax(0, ${editorUi.preview_ratio}fr) minmax(0, ${100 - editorUi.preview_ratio}fr)`
                : `minmax(0, ${100 - editorUi.preview_ratio}fr) minmax(0, ${editorUi.preview_ratio}fr)`,
            }}
          >
            {/* LEFT (or RIGHT when flipped) — Controls (metadata + TOC + inline editor) */}
            <div data-testid="editor-pane" className="flex flex-col min-h-0" style={{ order: editorUi.flip_panes ? 2 : 1 }}>
              {/* TOP: metadata + TOC (scrollable). Hidden entirely when editor_full_pane is on. */}
              <div className={`overflow-y-auto p-6 space-y-4 ${
                editingSection
                  ? (editorUi.editor_full_pane ? "hidden" : "flex-none h-[45%]")
                  : "flex-1"
              }`}>
                <BlogEditorMetadata
                  blog={blog}
                  authors={authors}
                  updateField={updateField}
                  reuploading={reuploading}
                  replaceDocx={replaceDocx}
                  setBlog={setBlog}
                />

                <BlogEditorTOC
                  sections={blog.sections}
                  editingSectionId={editingSectionId}
                  SubtypeMenu={SubtypeMenu}
                  InsertDivider={InsertDivider}
                  addSection={addSection}
                  openSection={openSection}
                  scrollPreviewTo={scrollPreviewTo}
                  updateSection={updateSection}
                  removeSectionWithToast={removeSectionWithToast}
                  confirmDelete={confirmDelete}
                />

                {/* Editor workspace preferences — mirrors the "Blog Editor
                   settings" admin panel. Changes persist globally via the same
                   API so admins can tune from either place. */}
                <details data-testid="editor-inline-ui-prefs" className="border border-slate-200 rounded-md bg-slate-50/50">
                  <summary className="cursor-pointer px-3 py-2 text-xs uppercase tracking-widest text-slate-500 hover:text-slate-800 list-none flex items-center justify-between">
                    <span className="inline-flex items-center gap-2">
                      <Maximize2 className="w-3.5 h-3.5"/>Workspace preferences
                    </span>
                    <ChevronDown className="w-3.5 h-3.5"/>
                  </summary>
                  <div className="px-3 py-3 space-y-3 border-t border-slate-200">
                    <div className="flex items-center gap-3">
                      <ArrowLeftRight className="w-4 h-4 text-sky-700 shrink-0"/>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-900">Flip panes</div>
                        <div className="text-xs text-slate-500">Put controls on the right, live preview on the left.</div>
                      </div>
                      <Switch data-testid="inline-editor-flip"
                              checked={editorUi.flip_panes}
                              onCheckedChange={v => setEditorUiField({ flip_panes: v })}/>
                    </div>
                    <div className="flex items-center gap-3">
                      <Maximize2 className="w-4 h-4 text-sky-700 shrink-0"/>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-900">Editor full pane when editing</div>
                        <div className="text-xs text-slate-500">Hide metadata + TOC while a section is open.</div>
                      </div>
                      <Switch data-testid="inline-editor-full-pane"
                              checked={editorUi.editor_full_pane}
                              onCheckedChange={v => setEditorUiField({ editor_full_pane: v })}/>
                    </div>
                    <div>
                      <div className="flex items-baseline gap-2">
                        <div className="text-sm text-slate-900 flex-1">Preview pane width</div>
                        <div className="text-xs tabular-nums text-slate-500">{editorUi.preview_ratio}%</div>
                      </div>
                      <div className="text-xs text-slate-500 mb-2">Slide right → live preview grows · Slide left → controls grow.</div>
                      <input type="range" min="30" max="70" step="5"
                             value={editorUi.preview_ratio}
                             onChange={e => setEditorUiField({ preview_ratio: Number(e.target.value) })}
                             className="w-full"
                             data-testid="inline-editor-preview-ratio"/>
                    </div>
                  </div>
                </details>
              </div>

              {/* BOTTOM: inline section editor (only when a section is open) */}
              {editingSection && (
                <BlogEditorInlineSection
                  ref={sectionEditorRef}
                  section={editingSection}
                  index={editingIndex}
                  updateSection={updateSection}
                  setSection={(next) => {
                    const arr = [...blog.sections];
                    arr[editingIndex] = next;
                    setBlog({ ...blog, sections: arr });
                    setDirty(true);
                  }}
                  confirmDelete={confirmDelete}
                  removeSectionWithToast={removeSectionWithToast}
                  onClose={() => setEditingSectionId(null)}
                />
              )}
            </div>

            {/* RIGHT — Live preview (mirrors BlogReader.jsx article structure) */}
            <BlogEditorPreview
              ref={previewPaneRef}
              blog={blog}
              authors={authors}
              editingSectionId={editingSectionId}
              veSettings={veSettings}
              previewRefs={previewRefs}
              InsertDivider={InsertDivider}
              openSection={openSection}
              confirmDelete={confirmDelete}
              removeSectionWithToast={removeSectionWithToast}
              scrollPreviewTo={scrollPreviewTo}
              order={editorUi.flip_panes ? 1 : 2}
            />
          </div>
        </DragDropContext>

        <DialogFooter className="px-6 py-4 border-t border-slate-200 shrink-0">
          <Button variant="outline" onClick={() => { if (dirty) setConfirmClose(true); else onOpenChange(false); }}>Cancel</Button>
          <Button data-testid="edit-blog-save" onClick={save} disabled={saving} className="bg-sky-700 hover:bg-sky-600">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {confirmClose && createPortal(
      <div className="fixed inset-0 z-[9999] bg-black/50 grid place-items-center p-4" style={{pointerEvents:'auto'}} onClick={() => setConfirmClose(false)}>
        <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg p-6 max-w-sm w-full shadow-xl">
          <div className="font-heading text-lg font-semibold text-slate-900">Unsaved changes</div>
          <div className="text-sm text-slate-600 mt-1">You have unsaved changes. Save them before closing?</div>
          <div className="mt-6 flex flex-wrap gap-2 justify-end">
            <Button variant="outline" onClick={() => setConfirmClose(false)}>Keep editing</Button>
            <Button variant="outline" className="text-red-600 border-red-300" onClick={() => { setConfirmClose(false); setDirty(false); onOpenChange(false); }}>Discard</Button>
            <Button className="bg-sky-700 hover:bg-sky-600" onClick={async () => { setConfirmClose(false); await save(); }}>Save & close</Button>
          </div>
        </div>
      </div>
    , document.body)}

    {confirmState.open && createPortal(
      <div data-testid="confirm-delete-dialog" className="fixed inset-0 z-[9999] bg-black/50 grid place-items-center p-4" style={{pointerEvents:'auto'}}
           onClick={() => setConfirmState({ open: false, title: "", body: "", onConfirm: null })}>
        <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg p-6 max-w-sm w-full shadow-xl">
          <div className="font-heading text-lg font-semibold text-slate-900">{confirmState.title}</div>
          <div className="text-sm text-slate-600 mt-1">{confirmState.body}</div>
          <div className="mt-6 flex gap-2 justify-end">
            <Button data-testid="confirm-cancel" variant="outline" onClick={() => setConfirmState({ open: false, title: "", body: "", onConfirm: null })}>Cancel</Button>
            <Button data-testid="confirm-delete-confirm" className="bg-red-600 hover:bg-red-500 text-white"
                    onClick={() => {
                      const fn = confirmState.onConfirm;
                      setConfirmState({ open: false, title: "", body: "", onConfirm: null });
                      fn?.();
                    }}>Delete</Button>
          </div>
        </div>
      </div>
    , document.body)}
  </>);
}
